import { Typography } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { getProduct } from '../../../requests';
import { ProductInfo, EntryValueDetails } from '../../../types';
import { MiraFieldProps } from './ComponentTypes';
import SubmitDialog from '../../SubmitDialog';
import { getMiraElementFormatter } from 'src/utils/mira';
import { useLookupRequestState } from './useLookupRequestState';
import { useMiraLookupFieldSync } from './useMiraLookupFieldSync';
import { LookupIdField } from './LookupIdField';
import { runLookupRequest } from './runLookupRequest';

const ProductId: React.FC<MiraFieldProps> = ({
  label,
  onChange,
  errors,
  value,
  prop,
  setValue,
  formData
}) => {
  const [productId, setProductId] = useState('');
  const [productName, setProductName] = useState('');
  const [productDeliveryAddress, setProductDeliveryAddress] = useState('');
  const [noCurrentRentModal, setNoCurrentRentModal] = useState(false);
  const [noPreviousRentModal, setNoPreviousRentModal] = useState(false);
  const miraSearchEnabled = prop.props?.miraSearchEnabled ?? false;
  const {
    isLoading,
    notFound: productNotFound,
    requestError: productGetError,
    beginRequest,
    markNotFound,
    markRequestError,
    endRequest
  } = useLookupRequestState();

  const { clearLookupFields, setLookupFieldsFromResponse } =
    useMiraLookupFieldSync({
      formData,
      setValue,
      source: 'product'
    });

  const clearValues = () => {
    setProductName('');
    setProductDeliveryAddress('');
    clearLookupFields();
  };

  useEffect(() => {
    fetchProductData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const fetchProductData = (
    fetchCurrentRent = prop.props?.currentlyRented ?? false
  ) => {
    const setValueDetails = (response: ProductInfo) => {
      const productName = response?.LRTInventSerialName;
      const productDeliveryAddress = response?.FormattedDelveryAddress;
      const hasProductName = productName !== undefined && productName !== null;
      const hasProductDeliveryAddress =
        productDeliveryAddress !== undefined && productDeliveryAddress !== null;
      const valueDetails: EntryValueDetails | undefined =
        hasProductName || hasProductDeliveryAddress
          ? {
              ...(hasProductName ? { productName } : {}),
              ...(hasProductDeliveryAddress ? { productDeliveryAddress } : {})
            }
          : undefined;

      setValue(
        prop.id.toString(),
        {
          value: productId,
          ...(valueDetails ? { valueDetails } : {})
        },
        {
          shouldValidate: true
        }
      );
    };

    runLookupRequest({
      isEnabled: Boolean(productId && miraSearchEnabled),
      clearValues,
      beginRequest,
      endRequest,
      markNotFound,
      markRequestError,
      onBeforeFetch: () => {
        setNoCurrentRentModal(false);
        setNoPreviousRentModal(false);
      },
      fetchLookup: () => getProduct(productId, fetchCurrentRent),
      onSuccess: (response) => {
        const resolvedProductName =
          response?.LRTInventSerialName ??
          `Tuotteelle ${productId} ei ole asetettu nimeä`;

        setProductName(resolvedProductName);
        setProductDeliveryAddress(response?.FormattedDelveryAddress ?? '');
        setLookupFieldsFromResponse(response, (miraFieldName, fieldValue) => {
          const formatter = getMiraElementFormatter(miraFieldName);
          return formatter(fieldValue);
        });
        setValueDetails(response);
        if (response?.RefIdReceipt === null) {
          fetchCurrentRent
            ? setNoCurrentRentModal(true)
            : setNoPreviousRentModal(true);
        }
      }
    });
  };

  return (
    <>
      <LookupIdField
        label={label}
        value={value}
        onChange={onChange}
        errors={errors}
        prop={prop}
        lookupId={productId}
        setLookupId={setProductId}
        isLoading={isLoading}
        notFound={productNotFound}
        requestError={productGetError}
        notFoundMessage={`Tuotekoodilla ${productId} ei löytynyt tuotetta Mirasta.`}
        requestErrorMessage={
          'Tuotteen hakemisessa tapahtui virhe. Tuotetiedot joudutaan syöttämään käsin.'
        }
        lookupDetails={[
          {
            key: 'product-name',
            label: 'Merkki ja malli:',
            value: productName
          }
        ]}
        onClearValues={clearValues}
      />
      <SubmitDialog
        show={noCurrentRentModal}
        headerText="Haluatko hakea edellisen vuokrauksen tiedot?"
        leavePageButtonText="Hae"
        stayPageButtonText="Peruuta"
        status="error"
        handleClose={() => {
          setNoCurrentRentModal(false);
        }}
        handleDismiss={() => {
          setNoCurrentRentModal(false);
          fetchProductData(false);
        }}
        handleStay={() => {
          setNoCurrentRentModal(false);
        }}
      >
        <Typography>
          <p>
            Sarjanumerolla {productId} ei löytynyt nykyisen vuokran
            vuokratietoja
          </p>
          <p>Haluatko hakea edellisen vuokran vuokratiedot?</p>
        </Typography>
      </SubmitDialog>
      <SubmitDialog
        show={noPreviousRentModal}
        headerText="Edellisen vuokran vuokratietoja ei löytynyt"
        leavePageButtonText="Selvä"
        status="error"
        handleClose={() => {
          setNoPreviousRentModal(false);
        }}
        handleDismiss={() => {
          setNoPreviousRentModal(false);
        }}
      >
        <Typography>
          <p>
            Sarjanumerolla {productId} ei löytynyt edellisen vuokran
            vuokratietoja
          </p>
        </Typography>
      </SubmitDialog>
    </>
  );
};

export { ProductId };
